import React from 'react';
import { Box, Typography } from '@mui/material';
import { CheckRounded } from '@mui/icons-material';
import { auth, fadeIn, gradientText, reducedMotion, riseIn } from './authTokens';
import StratifyGlyph from './StratifyGlyph';

/** The systems that report in during the handoff, in the order they light up. */
const LAUNCH_MODULES = ['Finance', 'Procurement', 'Inventory', 'Sales', 'Production', 'Projects'];

/**
 * Post-authentication transition.
 *
 * Credentials are already accepted by the time this mounts — it is played from
 * the `onBeforeSuccess` hook that `AuthContext.login` awaits before flipping
 * auth state, so it covers the handoff rather than delaying a decision. A wrong
 * password never reaches it: the callback is skipped on error.
 */
const LaunchOverlay: React.FC<{ phase: 'authing' | 'ready' }> = ({ phase }) => {
  // Circumference of the r=54 ring, so the dash maths is exact rather than eyeballed.
  const C = 2 * Math.PI * 54;

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 4000,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 4,
        backgroundColor: auth.ink,
        background: auth.pageGradient,
        color: auth.text,
        animation: `${fadeIn} .35s ease`,
        '@keyframes ringFill': { from: { strokeDashoffset: C }, to: { strokeDashoffset: 0 } },
        '@keyframes chipOn': {
          from: { opacity: 0.35, transform: 'translateY(4px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        ...reducedMotion,
      }}
    >
      {/* Ring + mark */}
      <Box
        sx={{
          position: 'relative',
          width: 148,
          height: 148,
          display: 'grid',
          placeItems: 'center',
          animation: `${riseIn} .4s ease both`,
          ...reducedMotion,
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 120 120"
          aria-hidden
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', transform: 'rotate(-90deg)' }}
        >
          <circle cx={60} cy={60} r={54} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={3} />
          <circle
            cx={60}
            cy={60}
            r={54}
            fill="none"
            stroke={auth.accent}
            strokeWidth={3}
            strokeLinecap="round"
            strokeDasharray={C}
            // Inline rather than sx: the dash offset is derived from C at
            // runtime, and `forwards` is what leaves the ring filled.
            style={{ strokeDashoffset: 0, animation: 'ringFill 2s cubic-bezier(.16,1,.3,1) forwards' }}
          />
        </Box>
        <StratifyGlyph size={52} />
      </Box>

      {/* Status */}
      <Box sx={{ textAlign: 'center' }} aria-live="polite">
        {phase === 'ready' ? (
          <>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                animation: `${riseIn} .35s ease both`,
                ...reducedMotion,
              }}
            >
              <CheckRounded sx={{ fontSize: 22, color: '#34d399' }} />
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, letterSpacing: '0.06em', ...gradientText }}>
                STRATIFY ONLINE
              </Typography>
            </Box>
            <Typography sx={{ mt: 1, fontSize: '0.9375rem', color: auth.textMuted }}>
              Your business is ready.
            </Typography>
          </>
        ) : (
          <Typography
            sx={{
              fontSize: '1.0625rem',
              fontWeight: 700,
              letterSpacing: '0.16em',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              color: auth.accent,
            }}
          >
            AUTHENTICATING…
          </Typography>
        )}
      </Box>

      {/* Modules coming online */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1, maxWidth: 420, px: 2 }}>
        {LAUNCH_MODULES.map((m, i) => (
          <Box
            key={m}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.25,
              py: 0.5,
              borderRadius: 999,
              border: '1px solid rgba(52,211,153,0.3)',
              backgroundColor: 'rgba(52,211,153,0.08)',
              animation: `chipOn .4s ease ${0.15 + i * 0.24}s both`,
              '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 1 },
            }}
          >
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#34d399' }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: auth.text }}>{m}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default LaunchOverlay;
